import React from 'react';
import { Rnd } from 'react-rnd';

import './FloatingWindow.css';

// FloatingWindow — react-rnd wrapper for Desktop preset windows.
//
// Props:
//   name      panel name, used as the key into the floating-
//             windows state and as the argument to onFocus / onChange.
//   title     human-readable label rendered in the title bar.
//   state     { x, y, width, height, z } from useFloatingWindows.
//             If undefined, the window renders nothing (defensive
//             — hook should always supply state for visible panels).
//   onChange  (name, partial) => void, called on drag stop and
//             resize stop with the new x/y/width/height.
//   onFocus   (name) => void, called on mousedown anywhere on the
//             window to bring it to front.
//
// Drag is constrained to the title bar via dragHandleClassName.
// Bounds are constrained to the parent (`bounds="parent"`).
// Grid-snap is 10px in both axes — react-rnd handles it natively.
export function FloatingWindow({
    name, title, state, onChange, onFocus, children,
}) {
    if (!state) return null;
    return (
        <Rnd
            className={'floating-window floating-window--' + name}
            // react-rnd applies display:inline-block + position:absolute
            // to its outer element via inline style, which beats any
            // class CSS. Re-asserting display:flex + flex-direction:
            // column here is what lets .floating-window actually behave
            // as a column flex container, so the body's `flex: 1`
            // gives it a definite height and absolute children inside
            // body resolve to the body's content area (not the whole
            // window, which would overlap the title bar).
            style={{
                zIndex: state.z,
                display: 'flex',
                flexDirection: 'column',
            }}
            position={{ x: state.x, y: state.y }}
            size={{ width: state.width, height: state.height }}
            minWidth={200}
            minHeight={140}
            bounds={'parent'}
            dragHandleClassName={'floating-window__title'}
            dragGrid={[10, 10]}
            resizeGrid={[10, 10]}
            onMouseDown={() => onFocus(name)}
            onDragStop={(e, d) =>
                onChange(name, { x: d.x, y: d.y })}
            onResizeStop={(e, dir, ref, delta, pos) =>
                onChange(name, {
                    width: ref.offsetWidth,
                    height: ref.offsetHeight,
                    x: pos.x,
                    y: pos.y,
                })}>
            <div className={'floating-window__title'}
                 data-testid={'floating-window-title-' + name}>
                {title}
            </div>
            <div className={'floating-window__body'}>
                {children}
            </div>
        </Rnd>
    );
}

export default FloatingWindow;
